// 🚀 SERVICIO API: Integración MongoDB para InnovaTech Lab
// 📁 RUTA: /src/services/innovatechService.js

// 📱 Configuración inteligente de URL para desarrollo y producción
const getApiBaseUrl = () => {
  // 1. Si hay configuración explícita, usarla
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // 2. Detectar entorno por hostname
  const currentHost = window.location.hostname;
  const currentPort = window.location.port;
  const protocol = window.location.protocol;
  const isProduction = currentHost.includes('edutechpulselab.com');
  
  console.log(`[🌐 API CONFIG] Detectando entorno:`, {
    host: currentHost,
    port: currentPort,
    protocol,
    isProduction
  });
  
  // 3. URLs por entorno
  if (isProduction) {
    // En producción usar siempre HTTPS con proxy (sin puerto 3001)
    const productionUrl = `https://${currentHost}/api`;
    console.log(`[🌐 PRODUCCIÓN] URL configurada:`, productionUrl);
    return productionUrl;
  }
  
  // 4. Si estamos en localhost, usar localhost
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return 'http://localhost:3001/api';
  }
  
  // 5. Si estamos en una IP de red local, usar la misma IP para el backend
  if (currentHost.startsWith('192.168.') || currentHost.startsWith('10.') || currentHost.startsWith('172.')) {
    return `http://${currentHost}:3001/api`;
  }
  
  // 6. Fallback por defecto
  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();
import { getSafeToken } from '../utils/tokenHelper';

// 🔍 Debug: Log de configuración
console.log('[🌐 API CONFIG] URL configurada:', API_BASE_URL, {
  isMobile: /Mobi|Android/i.test(navigator.userAgent),
  hostname: window.location.hostname,
  envVar: import.meta.env.VITE_API_URL
});

class InnovatechService {
  
  // Obtener token de autenticación
  getAuthToken() {
    return getSafeToken();
  }

  // Headers comunes para requests
  getHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // =============================================================================
  // SESSIONS - Gestión de sesiones de laboratorio
  // =============================================================================

  async createSession(sessionData) {
    try {
      const response = await fetch(`${API_BASE_URL}/innovatech/sessions`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          session_id: sessionData.sessionId || `sess_${Date.now()}`,
          lab_id: sessionData.labId || 'innovatech_lab',
          step_id: sessionData.stepId || 'contexto',
          mode: sessionData.mode || 'individual',
          json_block_hash: sessionData.configHash || 'default_hash',
          ip_address: sessionData.ipAddress,
          user_agent: navigator.userAgent,
          platform: 'web'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error creando sesión');
      }

      console.log('🚀🆕 [API SESIÓN] Sesión creada en backend:', data.session.session_id);
      return data;
      
    } catch (error) {
      console.error('[❌ API] Error creando sesión:', error);
      throw error;
    }
  }

  async getSession(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/innovatech/sessions/${sessionId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error obteniendo sesión');
      }

      return data;
      
    } catch (error) {
      console.error('[❌ API] Error obteniendo sesión:', error);
      throw error;
    }
  }

  async updateSessionProgress(sessionId, progressData) {
    try {
      const response = await fetch(`${API_BASE_URL}/innovatech/sessions/${sessionId}/progress`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(progressData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error actualizando progreso');
      }

      return data;
      
    } catch (error) {
      console.error('[❌ API] Error actualizando progreso:', error);
      throw error;
    }
  }

  // =============================================================================
  // MESSAGES - Gestión de input del usuario
  // =============================================================================

  async saveMessage(messageData) {
    try {
      const response = await fetch(`${API_BASE_URL}/innovatech/messages`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          session_id: messageData.sessionId,
          lab_id: messageData.labId || 'innovatech_lab',
          step_id: messageData.stepId,
          content: messageData.content,
          tokens_count: Math.ceil(messageData.content.length / 4),
          language: 'es',
          word_count: messageData.content.trim().split(/\s+/).filter(w => w.length > 0).length,
          client_ts: new Date().toISOString(),
          event_type: messageData.eventType || 'input_libre',
          input_method: 'typing'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error guardando mensaje');
      }

      console.log('📝✅ [API MENSAJE] Mensaje guardado en backend:', {
        sessionId: messageData.sessionId,
        stepId: messageData.stepId,
        isDuplicate: data.isDuplicate || false
      });

      return data;
      
    } catch (error) {
      console.error('[❌ API] Error guardando mensaje:', error);
      throw error;
    }
  }

  async getMessages(sessionId, stepId = null) {
    try {
      const url = new URL(`${API_BASE_URL}/innovatech/messages/${sessionId}`);
      if (stepId) {
        url.searchParams.append('step_id', stepId);
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error obteniendo mensajes');
      }

      return data;
      
    } catch (error) {
      console.error('[❌ API] Error obteniendo mensajes:', error);
      throw error;
    }
  }

  // =============================================================================
  // FEEDBACK - Gestión de respuestas de IA
  // =============================================================================

  async saveFeedback(feedbackData) {
    try {
      const response = await fetch(`${API_BASE_URL}/innovatech/feedback`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(feedbackData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error guardando feedback');
      }

      console.log('🤖✅ [API FEEDBACK] Feedback IA guardado en backend:', {
        sessionId: data.feedback?.session_id,
        stepId: data.feedback?.step_id,
        aiRole: data.feedback?.ai_role,
        score: data.feedback?.evaluation?.scores?.total
      });

      return data;
      
    } catch (error) {
      console.error('[❌ API] Error guardando feedback:', error);
      throw error;
    }
  }

  async getFeedback(sessionId, stepId = null) {
    try {
      const url = new URL(`${API_BASE_URL}/innovatech/feedback/${sessionId}`);
      if (stepId) {
        url.searchParams.append('step_id', stepId);
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error obteniendo feedback');
      }

      return data;
      
    } catch (error) {
      console.error('[❌ API] Error obteniendo feedback:', error);
      throw error;
    }
  }

  // =============================================================================
  // AI PROCESSING - Procesamiento con IA integrado
  // =============================================================================

  async processWithAI(requestData, retryCount = 0) {
    const maxRetries = 2;
    const retryDelay = 1000; // 1 segundo entre reintentos
    
    try {
      // 📱 Configurar timeout específico para móvil - Aumentado temporalmente para IA
      const isMobile = /Mobi|Android/i.test(navigator.userAgent);
      const timeoutMs = isMobile ? 30000 : 25000; // Timeout aumentado para IA compleja
      
      console.log(`[🤖 API] Procesando con IA (intento ${retryCount + 1}/${maxRetries + 1}):`, {
        url: `${API_BASE_URL}/ai/process`,
        stepId: requestData.stepId,
        timeoutMs: timeoutMs,
        contentLength: requestData.content?.length || 0
      });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      const response = await fetch(`${API_BASE_URL}/ai/process`, {
        method: 'POST',
        headers: this.getHeaders(),
        signal: controller.signal,
        body: JSON.stringify({
          hypothesis: requestData.content,  // 🔧 Campo correcto para aiController
          lab_id: requestData.labId || 'lab1',  // 🔧 Formato correcto
          step_id: requestData.stepId || 'hipotesis_central',
          session_id: requestData.sessionId
        })
      });

      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error procesando con IA');
      }

      // 🔍 Log seguro para JSON o String - Compatible con contrato v1.0
      let contentForPreview;
      
      // Detectar contrato v1.0 y extraer contenido correctamente
      if (data.contract_version === "1.0") {
        contentForPreview = data.payload?.output || data.payload?.content || {};
      } else {
        contentForPreview = data.content || {};
      }
      
      const contentPreview = typeof contentForPreview === 'string' 
        ? contentForPreview.substring(0, 80) + '...'
        : JSON.stringify(contentForPreview || {}).substring(0, 80) + '...';
      console.log(`[🤖 USER ACTION] IA respuesta:`, contentPreview); // ✅ Solo para acciones usuario
      return data;
      
    } catch (error) {
      console.error(`[❌ AI] Error procesando con IA (intento ${retryCount + 1}):`, {
        message: error.message,
        name: error.name,
        isAbortError: error.name === 'AbortError',
        isNetworkError: error.name === 'TypeError' && error.message.includes('fetch')
      });
      
      // 🔄 Retry para errores temporales
      const shouldRetry = (
        retryCount < maxRetries && 
        (
          error.name === 'AbortError' ||  // Timeout
          (error.name === 'TypeError' && error.message.includes('fetch')) ||  // Network error
          error.message.includes('ECONNRESET') ||  // Connection reset
          error.message.includes('ENOTFOUND')  // DNS error
        )
      );
      
      if (shouldRetry) {
        console.log(`[🔄 RETRY] Reintentando en ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.processWithAI(requestData, retryCount + 1);
      }
      
      // Si no se puede reintentar, lanzar el error original
      throw error;
    }
  }

  // =============================================================================
  // HELPERS - Funciones auxiliares
  // =============================================================================

  // Generar ID único para sesión
  generateSessionId(labId = 'innovatech_lab') {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `sess_${labId}_${timestamp}_${random}`;
  }

  // Obtener o crear ID de sesión desde localStorage como fallback
  getOrCreateSessionId(labId = 'innovatech_lab') {
    const storageKey = `sessionId_${labId}`;
    let sessionId = localStorage.getItem(storageKey);
    
    if (!sessionId) {
      sessionId = this.generateSessionId(labId);
      localStorage.setItem(storageKey, sessionId);
    }
    
    return sessionId;
  }

  // Limpiar datos de sesión local
  clearLocalSession(labId = 'innovatech_lab') {
    localStorage.removeItem(`sessionId_${labId}`);
    localStorage.removeItem(`inputLibre_${labId}`);
    localStorage.removeItem(`responses_${labId}`);
    console.log('[🗑️ LOCAL] Datos locales limpiados');
  }

  // Sincronizar datos con localStorage (temporal durante migración)
  syncWithLocalStorage(labId = 'innovatech_lab') {
    const sessionId = this.getOrCreateSessionId(labId);
    const inputLibre = localStorage.getItem(`inputLibre_${labId}`) || '';
    const responses = JSON.parse(localStorage.getItem(`responses_${labId}`) || '{}');
    
    return {
      sessionId,
      inputLibre,
      responses
    };
  }

  // =============================================================================
  // AI SUGGESTIONS - Sugerencias contextualizadas por IA
  // =============================================================================

  async generateSuggestions(suggestionData) {
    try {
      console.log(`[🎯 AI SUGGESTIONS] Solicitando ${suggestionData.suggestion_type} usando JSON centralizado`);
      
      // 🚀 PRIMERA OPCIÓN: Usar backend centralizado con JSON
      const response = await fetch(`${API_BASE_URL}/ai/suggestions`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          hypothesis: suggestionData.hypothesis,
          mvp_description: suggestionData.mvp_description,
          suggestion_type: suggestionData.suggestion_type, // 'metricas_clave' | 'metodos_medicion'
          session_id: suggestionData.session_id,
          use_centralized_prompts: true // ← Nuevo flag para usar JSON
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.warn('[⚠️ AI SUGGESTIONS] Backend no disponible, usando fallback del JSON');
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`[✅ AI SUGGESTIONS] Recibidas del JSON centralizado: ${data.suggestions?.length || 0} sugerencias`);
      
      // Procesar sugerencias para asegurar estructura consistente
      if (data.suggestions && Array.isArray(data.suggestions)) {
        data.suggestions = data.suggestions.map((suggestion, index) => {
          // Si es un string, convertir a objeto
          if (typeof suggestion === 'string') {
            return {
              name: suggestion,
              description: `Sugerencia IA: ${suggestion}`,
              why_relevant: "Generado por IA usando JSON centralizado",
              difficulty: index < 2 ? "fácil" : index < 4 ? "medio" : "avanzado"
            };
          }
          // Si ya es objeto, asegurar que tenga todas las propiedades
          return {
            name: suggestion.name || suggestion,
            description: suggestion.description || `Sugerencia: ${suggestion.name || suggestion}`,
            why_relevant: suggestion.why_relevant || "Generado por IA",
            difficulty: suggestion.difficulty || "medio"
          };
        });
      }
      
      return data;

    } catch (error) {
      console.error('[❌ AI SUGGESTIONS] Error con backend:', error);
      console.log('[🔄 AI SUGGESTIONS] Activando fallback del JSON centralizado');
      
      // 🔄 FALLBACK: Usar las sugerencias predefinidas del JSON (simulando carga del JSON)
      return {
        success: true,
        suggestions: this.getJSONFallbackSuggestions(suggestionData.suggestion_type),
        fallback: true,
        source: 'json_fallback'
      };
    }
  }

  // 🎯 NUEVO: Fallbacks del JSON centralizado (prompt_inovatech.json)
  getJSONFallbackSuggestions(suggestionType) {
    console.log('[📋 JSON FALLBACK] Usando sugerencias del JSON centralizado');
    
    if (suggestionType === 'metricas_clave') {
      // Estas son las fallback_suggestions del JSON - convertidas a objetos esperados
      const rawSuggestions = [
        "Tasa de conversión de visitante a registro",
        "Tiempo promedio hasta primera acción clave", 
        "Porcentaje de usuarios que completan el flujo principal",
        "Tasa de retención a 7 días",
        "Número de interacciones por sesión",
        "Porcentaje de usuarios que regresan en 48h"
      ];
      
      // Convertir strings a objetos con la estructura esperada
      return rawSuggestions.map((suggestion, index) => ({
        name: suggestion,
        description: `Métrica clave para validar tu hipótesis: ${suggestion}`,
        why_relevant: "Extraído del JSON centralizado",
        difficulty: index < 2 ? "fácil" : index < 4 ? "medio" : "avanzado"
      }));
    }

    if (suggestionType === 'metodos_medicion') {
      // Estas son las fallback_suggestions del JSON - convertidas a objetos esperados
      const rawSuggestions = [
        "Google Analytics con eventos personalizados",
        "Encuesta corta de 2-3 preguntas al finalizar flujo",
        "Registro manual de interacciones durante pruebas usuario", 
        "Heatmaps con Hotjar o similar en páginas clave",
        "A/B testing con variaciones mínimas del MVP"
      ];
      
      // Convertir strings a objetos con la estructura esperada
      return rawSuggestions.map((suggestion, index) => ({
        name: suggestion,
        description: `Método efectivo para medir: ${suggestion}`,
        why_relevant: "Extraído del JSON centralizado",
        difficulty: index < 2 ? "fácil" : index < 3 ? "medio" : "avanzado"
      }));
    }

    return [];
  }

  // 🔄 LEGACY: Sugerencias de fallback mejoradas (DEPRECATED - usar getJSONFallbackSuggestions)
  getFallbackSuggestions(suggestionType) {
    console.warn('[⚠️ LEGACY] Usando getFallbackSuggestions - migrar a getJSONFallbackSuggestions');
    
    if (suggestionType === 'metricas_clave') {
      return [
        {
          name: "Tasa de conversión (%)",
          description: "Porcentaje de usuarios que completan la acción principal del MVP. Mide directamente si tu solución genera el comportamiento deseado.",
          why_relevant: "Valida si el MVP resuelve el problema identificado",
          difficulty: "fácil"
        },
        {
          name: "Click-through rate (CTR)",
          description: "Porcentaje de usuarios que hacen clic en elementos específicos. Indica el nivel de interés y engagement con tu propuesta.",
          why_relevant: "Mide atractivo inicial de la solución",
          difficulty: "fácil"
        },
        {
          name: "Tiempo hasta primera acción",
          description: "Cuánto tiempo tardan los usuarios en realizar la primera acción relevante. Indica si la propuesta de valor es clara.",
          why_relevant: "Evalúa claridad de la propuesta de valor",
          difficulty: "medio"
        }
      ];
    }

    if (suggestionType === 'metodos_medicion') {
      return [
        {
          name: "Google Analytics",
          description: "Herramienta gratuita para rastrear comportamiento en sitios web. Permite medir clics, conversiones y embudos de usuario.",
          why_relevant: "Captura datos objetivos de comportamiento digital",
          difficulty: "fácil"
        },
        {
          name: "Entrevistas cortas",
          description: "Conversaciones de 5-10 minutos con usuarios después de usar el MVP. Permite entender el 'por qué' detrás de las métricas.",
          why_relevant: "Proporciona contexto cualitativo a los datos",
          difficulty: "medio"
        },
        {
          name: "Formulario post-uso",
          description: "Encuesta breve (2-3 preguntas) que aparece después de completar una acción. Captura feedback inmediato.",
          why_relevant: "Obtiene retroalimentación en el momento exacto",
          difficulty: "fácil"
        }
      ];
    }

    return [];
  }

  // =============================================================================
  // QUIZ DINÁMICO - Generación de preguntas personalizadas
  // =============================================================================

  async generateDynamicQuizQuestions(quizData) {
    try {
      console.log('[📝 QUIZ SERVICE] Solicitando preguntas dinámicas...');
      
      const response = await fetch(`${API_BASE_URL}/asistentes/innovatech/generate-quiz`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(quizData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error generando preguntas dinámicas');
      }

      console.log('[📝 QUIZ SERVICE] Preguntas dinámicas generadas exitosamente');
      return data;

    } catch (error) {
      console.error('[❌ QUIZ SERVICE ERROR]:', error.message);
      throw error;
    }
  }
}

export default new InnovatechService();